import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, Paperclip } from 'lucide-react';
import RichCommentEditor from './RichCommentEditor';
import { userApi, commentApi } from '../services/api';
import { User, Comment } from '../types';

interface RichCommentBoxProps {
  taskId?: number;
  caseId?: number;
  onCommentCreated: (comment?: Comment) => void;
  parentId?: number | null; // optional for replies
}

const RichCommentBox: React.FC<RichCommentBoxProps> = ({ taskId, caseId, onCommentCreated, parentId = null }) => {
  const [rawText, setRawText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<Array<{ url: string; name: string; type: string; size: number }>>([]);

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const fetchedUsers = await userApi.search('');
        setUsers(fetchedUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  const handleEditorChange = useCallback((newRawText: string, newHtml: string) => {
    setRawText(newRawText);
    setHtmlContent(newHtml);
  }, []);

  const handleSearchUsers = useCallback(async (query: string) => {
    return await userApi.search(query);
  }, []);

  const handleSubmit = async () => {
    if (!rawText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const mentionSpans = doc.querySelectorAll('span[data-type="mention"]');

      let processedHtml = htmlContent;
      let processedRawText = rawText;

      mentionSpans.forEach(span => {
        const id = span.getAttribute('data-id');
        const label = span.getAttribute('data-label');
        if (id && label) {
          const mentionLink = `<a class="mention" href="/users/${id}">@${label}</a>`;
          processedHtml = processedHtml.replace(span.outerHTML, mentionLink);
          const mentionToken = `@[${label}](user:${id})`;
          processedRawText = processedRawText.replace(`@${label}`, mentionToken);
        }
      });

      const payload: any = {
        raw_text: processedRawText,
        html: processedHtml,
      };
      if (taskId) payload.task_id = taskId;
      if (caseId) payload.case_id = caseId;
      if (parentId) payload.parent_id = parentId;

      // If there are files, send multipart form data
      let res;
      if (files.length > 0) {
        const form = new FormData();
        if (taskId) form.append('task_id', String(taskId));
        if (caseId) form.append('case_id', String(caseId));
        if (parentId) form.append('parent_id', String(parentId));
        form.append('raw_text', processedRawText);
        form.append('html', processedHtml);
        files.forEach((f) => form.append('files[]', f));
        res = await commentApi.createWithFiles(form);
      } else {
        res = await commentApi.create(payload);
      }

      setRawText('');
      setHtmlContent('');
      // cleanup previews and files
      try { previews.forEach(p => URL.revokeObjectURL(p.url)); } catch {}
      setPreviews([]);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const created = (res as any).comment || (res as any).data || undefined;
      onCommentCreated(created);
    } catch (error) {
      console.error('Failed to post comment:', error);
      onCommentCreated();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <RichCommentEditor
        value={htmlContent}
        onChange={handleEditorChange}
        onSearchUsers={handleSearchUsers}
        users={users}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            <Paperclip className="w-4 h-4 text-gray-500" />
          </motion.button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const list = e.target.files ? Array.from(e.target.files) : [];
              // cleanup existing object URLs
              previews.forEach(p => URL.revokeObjectURL(p.url));
              setFiles(list);
              const next = list.map(f => ({ url: URL.createObjectURL(f), name: f.name, type: f.type, size: f.size }));
              setPreviews(next);
            }}
          />
          {files.length > 0 && (
            <span className="text-xs text-gray-600">{files.length} file(s) selected</span>
          )}
        </div>
      </div>
      {previews.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {previews.map((p, idx) => {
            const isImage = p.type.startsWith('image/');
            return (
              <div key={idx} className="relative group border border-gray-200 rounded-md bg-white/70 p-1 flex items-center justify-center">
                {isImage ? (
                  <img src={p.url} alt={p.name} className="max-h-20 object-contain rounded" />
                ) : (
                  <div className="text-[10px] text-gray-700 px-2 py-4 text-center">
                    <div className="font-medium truncate max-w-[120px]">{p.name}</div>
                    <div className="text-gray-500">{(p.size/1024).toFixed(0)} KB</div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const nextFiles = files.slice();
                    nextFiles.splice(idx, 1);
                    setFiles(nextFiles);
                    const nextPrev = previews.slice();
                    URL.revokeObjectURL(nextPrev[idx].url);
                    nextPrev.splice(idx, 1);
                    setPreviews(nextPrev);
                  }}
                  className="absolute -top-1 -right-1 hidden group-hover:block bg-white border border-gray-300 text-[10px] rounded-full px-1 leading-none"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSubmit}
          disabled={!rawText.trim() || isSubmitting}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-xl shadow-medium hover:shadow-glow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
              <span>Posting…</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>{parentId ? 'Reply' : 'Comment'}</span>
            </>
          )}
        </motion.button>
    </div>
  );
};

export default RichCommentBox;
